import QuickOptions, { QuickOption } from '@Components/General/QuickOptions';
import {
    AddPhotoAlternateOutlined,
    AddRounded,
    DeleteOutlineRounded,
} from '@mui/icons-material';
import { Avatar, Badge, Icon, IconButton, useTheme } from '@mui/material';
import { stringToColor } from '@utils/colorUtils';
import { closeOptions } from '@utils/utils';
import { useRef } from 'react';
import { Control, FieldValues, useController, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const M3IconButton = ({ children, ...props }) => {
    const { variant = 'filled', color = 'primary', ...rest } = props;
    const theme = useTheme();
    const styles = theme.components?.MuiButton?.variants?.find(
        muiVariant =>
            muiVariant.props.color === color &&
            muiVariant.props.variant === variant,
    )?.style;

    return (
        <IconButton sx={styles} {...rest}>
            {children}
        </IconButton>
    );
};

interface ProfilePictureFieldProps {
    name: string;
    control: Control<FieldValues>;
    text?: string;
}

const ProfilePictureField: React.FC<ProfilePictureFieldProps> = ({
    name,
    control,
    text,
}) => {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const { field } = useController({
        control,
        name,
    });

    const { ref: registerRef, ...registerData } = control.register(name, {
        onChange: e => {
            if (e.target.files?.length) field.onChange(e.target.files?.[0]);
        },
    });

    const formProfilePicture = useWatch({ control, name });

    const profilePictureUrl =
        formProfilePicture instanceof File
            ? URL.createObjectURL(formProfilePicture)
            : formProfilePicture;

    const options: QuickOption[] = [
        {
            label: t('Change Picture'),
            onClick: () => inputRef.current.click(),
            Icon: AddPhotoAlternateOutlined,
        },
        {
            label: t('Remove Picture'),
            onClick: () => {
                field.onChange('');
                closeOptions();
            },
            Icon: DeleteOutlineRounded,
        },
    ];

    return (
        <QuickOptions options={options}>
            <Badge
                overlap="circular"
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                sx={{
                    cursor: 'pointer',
                    '& .MuiBadge-badge': {
                        height: 'unset',
                        borderRadius: '50%',
                        padding: 0.5,
                        backgroundColor: 'white',
                    },
                }}
                onClick={e => {
                    if (!profilePictureUrl) {
                        e.stopPropagation();
                        inputRef.current.click();
                    }
                }}
                badgeContent={
                    <M3IconButton
                        color="primary"
                        variant={profilePictureUrl ? 'tonal' : 'filled'}
                        size="medium"
                    >
                        {profilePictureUrl ? (
                            <Icon>edit_square</Icon>
                        ) : (
                            <AddRounded />
                        )}
                    </M3IconButton>
                }
            >
                <Avatar
                    src={profilePictureUrl}
                    sx={{
                        height: 160,
                        width: 160,
                        fontSize: 64,
                        bgcolor: text ? stringToColor(text) : undefined,
                    }}
                >
                    {text?.getNameInitials()}
                </Avatar>
            </Badge>
            <input
                type="file"
                accept="image/*"
                id="profile_picture_input"
                {...registerData}
                onClick={e => e.stopPropagation()}
                style={{ display: 'none' }}
                ref={e => {
                    registerRef(e);
                    inputRef.current = e;
                }}
            />
        </QuickOptions>
    );
};

export default ProfilePictureField;
